import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, AccountTree as AccountTreeIcon, Close as CloseIcon } from '@mui/icons-material';
import { useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { facilities, facilitiesScoped } from '@/api/platform';
import { DataTable } from '@/components/DataTable';
import { StatusPill } from '@/components/StatusPill';
import { TenantSelect } from '@/components/TenantSelect';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, relativeTime } from '@/utils/format';
import type { Facility } from '@/types/backend';

/**
 * Cross-tenant facilities list. Reads via the admin endpoint (PR-6).
 *
 * Mutations: the admin endpoint exposes only reads (`GET /admin/abdm/
 * facilities/...`); writes go through the tenant-scoped surface
 * (`/api/abdm/facilities/*`) which expects the X-Tenant-Id header. We
 * temporarily flip the axios `activeTenantId` for the duration of a
 * mutation so a Platform Admin can create/edit/deactivate any tenant's
 * facility without leaving this page. After the call we restore the
 * previous tenant.
 */

const facilitySchema = z.object({
  tenantId: z.string().min(1, 'tenantId is required'),
  facilityId: z.string().min(1, 'facilityId is required'),
  name: z.string().optional(),
  abdmFacilityCode: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  pincode: z.string().optional(),
  active: z.boolean(),
  primary: z.boolean().optional(),
});
type FacilityForm = z.infer<typeof facilitySchema>;

import { setActiveTenantId } from '@/api/client';

async function asTenant<T>(tenantId: string, fn: () => Promise<T>): Promise<T> {
  // The admin path doesn't accept tenant param on writes — temporarily
  // pose as that tenant via the axios request header for the duration
  // of the call, then restore.
  setActiveTenantId(tenantId);
  try {
    return await fn();
  } finally {
    setActiveTenantId(null);
  }
}

export function FacilitiesPage() {
  const theme = useTheme();
  const { data, error, isLoading } = useSWR(
    '/admin/abdm/facilities',
    () => facilities.list(),
  );

  const [editing, setEditing] = useState<Facility | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDeactivate, setPendingDeactivate] = useState<Facility | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = () => globalMutate('/admin/abdm/facilities');

  const handleCreate = async (form: FacilityForm) => {
    setActionError(null);
    try {
      await asTenant(form.tenantId, () => facilitiesScoped.create(form));
      setCreating(false);
      refresh();
    } catch (e) {
      setActionError((e as Error).message ?? 'Create failed.');
    }
  };

  const handleUpdate = async (form: FacilityForm, original: Facility) => {
    setActionError(null);
    try {
      await asTenant(original.tenantId, () =>
        facilitiesScoped.update(original.facilityId, form),
      );
      setEditing(null);
      refresh();
    } catch (e) {
      setActionError((e as Error).message ?? 'Update failed.');
    }
  };

  const handleDeactivate = async () => {
    if (!pendingDeactivate) return;
    await asTenant(pendingDeactivate.tenantId, () =>
      facilitiesScoped.deactivate(pendingDeactivate.facilityId),
    );
    refresh();
  };

  return (
    <PageShell
      kicker="PLATFORM · FACILITIES"
      title="Facilities"
      subtitle="Per-tenant clinics mapped to ABDM HIP/HIU bridges. Soft-delete via the active flag preserves audit history."
      width="wide"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
          sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          New facility
        </Button>
      }
    >
      <DataTable<Facility>
        rows={data}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        searchable
        searchPlaceholder="tenant, facility name, HIP/HIU id…"
        paginated
        empty={{
          icon: AccountTreeIcon,
          title: 'No facilities yet.',
          description: 'Add the first facility to map your tenant\'s clinics to ABDM bridges.',
          action: (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
              New facility
            </Button>
          ),
        }}
        columns={[
          {
            key: 'tenant',
            header: 'Tenant',
            sortable: true,
            sortValue: (r) => r.tenantId,
            filter: { type: 'select', getFilterValue: (r) => r.tenantId },
            cell: (r) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{redactMiddle(r.tenantId, 6, 6)}</Typography>,
            width: 160,
          },
          {
            key: 'facility',
            header: 'Facility',
            sortable: true,
            sortValue: (r) => r.name ?? r.facilityId,
            searchValue: (r) => `${r.name ?? ''} ${r.facilityId} ${r.abdmFacilityCode ?? ''}`,
            pinned: true,
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {r.name ?? r.facilityId}
                </Typography>
                <Typography variant="caption" color="text.tertiary">
                  {r.facilityId} {r.abdmFacilityCode ? `· HFR ${r.abdmFacilityCode}` : ''}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'hip-hiu',
            header: 'HIP / HIU',
            sortable: true,
            sortValue: (r) => `${r.hipId ?? ''} ${r.hiuId ?? ''}`,
            searchValue: (r) => `${r.hipId ?? ''} ${r.hiuId ?? ''}`,
            cell: (r) => (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: theme.typography.fontFamily }}>
                {r.hipId ? `HIP ${r.hipId}` : ''}
                {r.hipId && r.hiuId ? ' · ' : ''}
                {r.hiuId ? `HIU ${r.hiuId}` : ''}
                {!r.hipId && !r.hiuId && '—'}
              </Typography>
            ),
            width: 240,
          },
          {
            key: 'status',
            header: 'Status',
            sortable: true,
            sortValue: (r) => (r.active ? 1 : 0),
            filter: {
              type: 'select',
              options: [{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }],
              getFilterValue: (r) => (r.active ? 'active' : 'inactive'),
            },
            cell: (r) => (r.active ? <StatusPill tone="success">Active</StatusPill> : <StatusPill tone="mono">Inactive</StatusPill>),
            width: 110,
          },
          {
            key: 'updated',
            header: 'Updated',
            sortable: true,
            sortValue: (r) => r.updatedAt,
            cell: (r) => (
              <Typography variant="caption" color="text.secondary">
                {relativeTime(r.updatedAt)}
              </Typography>
            ),
            width: 140,
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            width: 220,
            pinned: true,
            cell: (r) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Button size="small" variant="text" sx={{ fontSize: 12 }} onClick={() => setEditing(r)}>
                  Edit
                </Button>
                {r.active && (
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    sx={{ fontSize: 12 }}
                    onClick={() => setPendingDeactivate(r)}
                  >
                    Deactivate
                  </Button>
                )}
              </Stack>
            ),
          },
        ]}
      />

      {actionError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Create */}
      <FacilityFormDialog
        open={creating}
        title="New facility"
        onClose={() => setCreating(false)}
        onSubmit={handleCreate}
      />

      {/* Edit */}
      <FacilityFormDialog
        open={!!editing}
        title="Edit facility"
        initial={editing ?? undefined}
        tenantLocked
        facilityLocked
        onClose={() => setEditing(null)}
        onSubmit={async (form) => { if (editing) await handleUpdate(form, editing); }}
      />

      {/* Deactivate */}
      <ConfirmDialog
        open={!!pendingDeactivate}
        onClose={() => setPendingDeactivate(null)}
        title="Deactivate this facility?"
        body={
          pendingDeactivate ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{pendingDeactivate.name ?? pendingDeactivate.facilityId}</strong> will stop receiving new traffic. Existing audit and care-context history is preserved.
              </Typography>
              <Typography variant="caption" color="text.tertiary">
                Tenant {redactMiddle(pendingDeactivate.tenantId, 6, 6)} · facility {pendingDeactivate.facilityId}
              </Typography>
            </>
          ) : null
        }
        actionLabel="Deactivate"
        tone="danger"
        onConfirm={handleDeactivate}
      />
    </PageShell>
  );
}

interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (form: FacilityForm) => void | Promise<void>;
  initial?: Facility;
  tenantLocked?: boolean;
  facilityLocked?: boolean;
}

function FacilityFormDialog({ open, title, onClose, onSubmit, initial, tenantLocked, facilityLocked }: DialogProps) {
  const theme = useTheme();
  const { control, handleSubmit, reset, formState: { isSubmitting, errors } } = useForm<FacilityForm>({
    defaultValues: {
      tenantId: initial?.tenantId ?? '',
      facilityId: initial?.facilityId ?? '',
      name: initial?.name ?? '',
      abdmFacilityCode: initial?.abdmFacilityCode ?? '',
      city: initial?.city ?? '',
      state: initial?.state ?? '',
      pincode: initial?.pincode ?? '',
      active: initial?.active ?? true,
      primary: initial?.primary ?? false,
    },
  });

  const onOpen = () => reset({
    tenantId: initial?.tenantId ?? '',
    facilityId: initial?.facilityId ?? '',
    name: initial?.name ?? '',
    abdmFacilityCode: initial?.abdmFacilityCode ?? '',
    city: initial?.city ?? '',
    state: initial?.state ?? '',
    pincode: initial?.pincode ?? '',
    active: initial?.active ?? true,
    primary: initial?.primary ?? false,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onTransitionEnter={onOpen}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        {title}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="facility-form" onSubmit={handleSubmit(async (form) => {
          const parsed = facilitySchema.safeParse(form);
          if (!parsed.success) return;
          await onSubmit(parsed.data);
        })}>
          <Stack spacing={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Controller
                name="tenantId"
                control={control}
                render={({ field }) => (
                  <TenantSelect
                    value={field.value}
                    onChange={field.onChange}
                    label="Tenant"
                    required
                    disabled={tenantLocked || isSubmitting}
                    fullWidth
                    allowAll={false}
                    helperText={errors.tenantId?.message ?? 'Owning tenant for this facility.'}
                  />
                )}
              />
              <Controller
                name="facilityId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Facility ID"
                    fullWidth
                    disabled={facilityLocked || isSubmitting}
                    error={!!errors.facilityId}
                    helperText={errors.facilityId?.message ?? 'Stable id within the tenant.'}
                  />
                )}
              />
            </Box>
            <Controller
              name="name"
              control={control}
              render={({ field }) => <TextField {...field} label="Clinic name" fullWidth helperText="The patient-facing name for this physical location." />}
            />
            <Alert severity="info" variant="outlined">
              Per ABDM, <strong>HIP-ID and HIU-ID are at the tenant level</strong> — one per legal entity.
              Every facility under this tenant inherits them automatically. Edit those values on the
              tenant record itself.
            </Alert>
            <Controller
              name="abdmFacilityCode"
              control={control}
              render={({ field }) => <TextField {...field} label="ABDM Facility Code (HFR)" fullWidth helperText="HFR-issued code unique to this physical clinic. Required for ABDM call-back routing." />}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 2 }}>
              <Controller name="city" control={control} render={({ field }) => <TextField {...field} label="City" fullWidth />} />
              <Controller name="state" control={control} render={({ field }) => <TextField {...field} label="State" fullWidth />} />
              <Controller name="pincode" control={control} render={({ field }) => <TextField {...field} label="PIN" fullWidth />} />
            </Box>
            <Controller
              name="primary"
              control={control}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Switch checked={!!field.value} onChange={(_, v) => field.onChange(v)} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Primary clinic</Typography>
                    <Typography variant="caption" color="text.tertiary">
                      Default destination when ABDM callbacks arrive without a facility code. Only one row per tenant should be primary.
                    </Typography>
                  </Box>
                </Stack>
              )}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button type="submit" form="facility-form" variant="contained" disabled={isSubmitting}>
          {initial ? 'Save changes' : 'Create facility'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
